import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button, TextInput } from '../../../components/ui';
import ContactPointsEditor from '../../address-book/ContactPointsEditor';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { moduleConfig } from '../config';
import { useClientContact, type ClientContact } from '../useData';
import type { ContactPointRow } from '../../address-book/contactPoints';

interface ClientContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  contactId?: number | null;
  onSaved: () => void;
}

type FormState = {
  clientContactFirstName: string;
  clientContactLastName: string;
  clientContactPosition: string;
  contactEmails: ContactPointRow[];
  contactPhones: ContactPointRow[];
  clientContactNotes: string;
};

const ClientContactFormModal: React.FC<ClientContactFormModalProps> = ({
  isOpen,
  onClose,
  clientId,
  contactId,
  onSaved,
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const isEdit = contactId != null && contactId > 0;
  const { data: existing, isLoading } = useClientContact(isEdit ? contactId : 0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => ({
    clientContactFirstName: '',
    clientContactLastName: '',
    clientContactPosition: '',
    contactEmails: [],
    contactPhones: [],
    clientContactNotes: '',
  }));

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && existing) {
      setForm(moduleConfig.mapItemToFormData(existing) as FormState);
      return;
    }
    if (!isEdit) {
      setForm({
        ...moduleConfig.getInitialFormData(),
        clientContactFirstName: '',
        clientContactLastName: '',
        clientContactPosition: '',
        contactEmails: [],
        contactPhones: [],
        clientContactNotes: '',
      } as FormState);
    }
  }, [isOpen, isEdit, existing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, clientId } as Partial<ClientContact>;
      if (isEdit && contactId) {
        await moduleConfig.form.update(contactId, payload);
        showSuccess('Person updated successfully');
      } else {
        await moduleConfig.form.create(payload);
        showSuccess('Person added successfully');
      }
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : moduleConfig.form.errorMessage;
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const formCopy = isEdit ? moduleConfig.form.edit : moduleConfig.form.new;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit}>
        <ModalHeader>
          <ModalTitle>{formCopy.title}</ModalTitle>
          <p className={cn('text-sm mt-1', colors.textMuted)}>{formCopy.subtitle}</p>
        </ModalHeader>
        <ModalContent>
          {isEdit && isLoading ? (
            <p className={cn('text-sm py-4', colors.textMuted)}>Loading...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={cn('block text-sm font-medium mb-1', colors.text)}>First name</label>
                  <TextInput
                    value={form.clientContactFirstName}
                    onChange={(e) => setForm((prev) => ({ ...prev, clientContactFirstName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-1', colors.text)}>Last name</label>
                  <TextInput
                    value={form.clientContactLastName}
                    onChange={(e) => setForm((prev) => ({ ...prev, clientContactLastName: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className={cn('block text-sm font-medium mb-1', colors.text)}>Position</label>
                <TextInput
                  value={form.clientContactPosition}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientContactPosition: e.target.value }))}
                />
              </div>
              <ContactPointsEditor
                kind="EMAIL"
                label="Emails"
                value={form.contactEmails}
                onChange={(contactEmails) => setForm((prev) => ({ ...prev, contactEmails }))}
              />
              <ContactPointsEditor
                kind="PHONE"
                label="Phones"
                value={form.contactPhones}
                onChange={(contactPhones) => setForm((prev) => ({ ...prev, contactPhones }))}
              />
              <div>
                <label className={cn('block text-sm font-medium mb-1', colors.text)}>Notes</label>
                <textarea
                  value={form.clientContactNotes}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientContactNotes: e.target.value }))}
                  rows={4}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm',
                    colors.border,
                    colors.background,
                    colors.text,
                  )}
                />
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            {moduleConfig.form.cancelText}
          </Button>
          <Button type="submit" variant="default" disabled={saving || (isEdit && isLoading)}>
            {saving ? moduleConfig.form.loadingText : formCopy.submitText}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ClientContactFormModal;
